import { createBigDecimal } from '@0xtorch/big-decimal'
import type { Transfer } from '@0xtorch/core'
import type { z } from 'zod'
import type { Chain } from '../../../../chain'
import type {
  Erc20TokenTransfer,
  Erc721TokenTransfer,
  Erc1155TokenTransfer,
  LowerHex,
  TransactionDecoded,
} from '../../../../types'
import { createEvmAddressId } from '../../../../utils'
import { getAllValue } from './allValue'
import { checkValueIsArrayIn } from './arrayIn'
import { checkValueIsBetweenBigInt } from './betweenBigInt'
import { generateCustomTransfer } from './customTransferGenerator'
import { generateTransfersByErc20Transfer } from './erc20TransferGenerator'
import { generateTransfersByErc721Transfer } from './erc721TransferGenerator'
import { generateTransfersByErc1155Transfer } from './erc1155TransferGenerator'
import { generateTransfersByInternalTransactions } from './internalTransferGenerator'
import type { transferGeneratorSchema } from './schema'

export const generateTransfers = ({
  schema,
  chain,
  transaction,
  relatedAddresses,
  erc20Target,
  erc721Target,
  erc1155Target,
}: {
  readonly schema: z.infer<typeof transferGeneratorSchema>
  readonly chain: Chain
  readonly transaction: TransactionDecoded
  readonly relatedAddresses: Set<LowerHex>
  readonly erc20Target?: Erc20TokenTransfer
  readonly erc721Target?: Erc721TokenTransfer
  readonly erc1155Target?: Erc1155TokenTransfer
}): readonly Transfer[] => {
  switch (schema.type) {
    case 'value': {
      // from
      if (
        schema.from !== undefined &&
        !checkValueIsArrayIn({
          value: transaction.from.address,
          schema: schema.from,
          transaction,
          relatedAddresses,
        })
      ) {
        return []
      }

      // to
      if (
        schema.to !== undefined &&
        !checkValueIsArrayIn({
          value: transaction.to?.address ?? '',
          schema: schema.to,
          transaction,
          relatedAddresses,
        })
      ) {
        return []
      }

      // value
      if (schema.value !== undefined) {
        if (typeof schema.value === 'string') {
          if (transaction.value.toString() !== schema.value) {
            return []
          }
        } else {
          switch (schema.value.type) {
            case 'between': {
              if (
                !checkValueIsBetweenBigInt({
                  value: transaction.value,
                  schema: schema.value,
                })
              ) {
                return []
              }
              break
            }
            case 'function':
            case 'from':
            case 'to':
            case 'value':
            case 'join':
            case 'slice':
            case 'chain-id':
            case 'tx-hash':
            case 'log':
            case 'erc20':
            case 'erc721':
            case 'erc1155':
            case 'math': {
              const value = getAllValue({
                schema: schema.value,
                transaction,
                relatedAddresses,
              })
              if (transaction.value.toString() !== value) {
                return []
              }
              break
            }
            default: {
              throw new Error(
                `Unknown value type: ${schema.value satisfies never}`,
              )
            }
          }
        }
      }

      return [
        {
          direction: schema.direction,
          from: createEvmAddressId({
            chainId: transaction.chainId,
            address: transaction.from.address,
          }),
          to:
            transaction.to === undefined
              ? undefined
              : createEvmAddressId({
                  chainId: transaction.chainId,
                  address: transaction.to.address,
                }),
          asset: chain.nativeToken.currency,
          amount: createBigDecimal(
            transaction.value,
            chain.nativeToken.decimals,
          ),
        },
      ]
    }
    case 'internal': {
      return generateTransfersByInternalTransactions({
        schema,
        chain,
        transaction,
        relatedAddresses,
      })
    }
    case 'erc20': {
      return generateTransfersByErc20Transfer({
        schema,
        transaction,
        relatedAddresses,
      })
    }
    case 'erc721': {
      return generateTransfersByErc721Transfer({
        schema,
        transaction,
        relatedAddresses,
      })
    }
    case 'erc1155': {
      return generateTransfersByErc1155Transfer({
        schema,
        transaction,
        relatedAddresses,
      })
    }
    case 'target': {
      if (erc20Target !== undefined) {
        return [
          {
            direction: schema.direction,
            from: createEvmAddressId({
              chainId: transaction.chainId,
              address: erc20Target.from,
            }),
            to: createEvmAddressId({
              chainId: transaction.chainId,
              address: erc20Target.to,
            }),
            asset: erc20Target.token.currency,
            amount: createBigDecimal(
              erc20Target.value,
              erc20Target.token.decimals,
            ),
          },
        ]
      }
      if (erc721Target !== undefined) {
        return [
          {
            direction: schema.direction,
            from: createEvmAddressId({
              chainId: transaction.chainId,
              address: erc721Target.from,
            }),
            to: createEvmAddressId({
              chainId: transaction.chainId,
              address: erc721Target.to,
            }),
            asset: erc721Target.token,
            amount: createBigDecimal(1n),
          },
        ]
      }
      if (erc1155Target !== undefined) {
        return [
          {
            direction: schema.direction,
            from: createEvmAddressId({
              chainId: transaction.chainId,
              address: erc1155Target.from,
            }),
            to: createEvmAddressId({
              chainId: transaction.chainId,
              address: erc1155Target.to,
            }),
            asset: erc1155Target.token,
            amount: createBigDecimal(erc1155Target.value),
          },
        ]
      }
      throw new Error('Target is not found')
    }
    case 'custom': {
      return [
        generateCustomTransfer({
          schema,
          chain,
          transaction,
          relatedAddresses,
        }),
      ]
    }
    default: {
      throw new Error(
        `Unknown transfer generator type: ${schema satisfies never}`,
      )
    }
  }
}
